import React, { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LogoWordmark, LogoMark } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { openCookiePreferences } from "@/lib/cookieConsent";
import { SUITE } from "@/lib/suite";
import LabsLink from "@/components/LabsLink";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Features", to: "/features" },
  { label: "Compare", to: "/compare" },
  { label: "Pricing", to: "/pricing" },
  { label: "Security", to: "/security" },
  { label: "Deploy", to: "/deploy" },
];

function NavItem({ to, label, onClick }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      onClick={onClick}
      className={({ isActive }) =>
        `text-sm font-medium transition-colors duration-200 hover:text-foreground ${
          isActive ? "text-foreground" : "text-muted-foreground"
        }`
      }
      data-testid={`nav-link-${label.toLowerCase()}`}
    >
      {label}
    </NavLink>
  );
}

export function MarketingLayout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <div className="suite-grid min-h-screen">
      <div className="bg-[#0b1426] text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-2 text-[12px] lg:px-8">
          <p className="truncate text-slate-200">
            The Pravadh suite — IQ for Salesforce change, PM for delivery, GovernX for limits.
          </p>
          <LabsLink
            arrow
            testid="nav-labs"
            className="inline-flex shrink-0 items-center gap-1 font-semibold text-white hover:underline"
          >
            Pravadh Labs
          </LabsLink>
        </div>
      </div>

      <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3.5 lg:px-8">
          <Link to="/" data-testid="nav-logo">
            <LogoWordmark size={34} />
          </Link>

          <nav className="hidden items-center gap-7 md:flex">
            {NAV.map((n) => (
              <NavItem key={n.to} {...n} />
            ))}
          </nav>

          <div className="hidden items-center gap-3 md:flex">
            <div className="hidden items-center gap-2 lg:flex" data-testid="nav-suite">
              {SUITE.filter((p) => !p.current).map((p) => (
                <a
                  key={p.id}
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground"
                >
                  {p.id.toUpperCase()}
                </a>
              ))}
              <span className="rounded-md bg-primary px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">GX</span>
            </div>
            <ThemeToggle />
            <Link to="/login">
              <Button variant="ghost" size="sm" className="text-foreground" data-testid="nav-signin-button">
                Log in
              </Button>
            </Link>
            <Link to="/signup">
              <Button size="sm" className="rounded-lg font-semibold" data-testid="nav-signup-button">
                Start free trial
              </Button>
            </Link>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <ThemeToggle />
            <Button
              variant="outline"
              size="icon"
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              aria-label="Menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {open && (
          <div className="border-t border-border bg-background px-6 py-4 md:hidden" data-testid="nav-mobile-menu">
            <div className="flex flex-col gap-4">
              {NAV.map((n) => (
                <NavItem key={n.to} {...n} onClick={() => setOpen(false)} />
              ))}
              <LabsLink
                arrow
                testid="nav-labs-mobile"
                className="inline-flex items-center gap-1 text-sm font-medium text-foreground"
                onClick={() => setOpen(false)}
              >
                Pravadh Labs
              </LabsLink>
              <div className="mt-2 flex gap-3">
                <Link to="/login" className="flex-1" onClick={() => setOpen(false)}>
                  <Button variant="outline" className="w-full">
                    Log in
                  </Button>
                </Link>
                <Link to="/signup" className="flex-1" onClick={() => setOpen(false)}>
                  <Button className="w-full font-semibold">Start free trial</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      <main key={pathname}>
        <Outlet />
      </main>

      <MarketingFooter />
    </div>
  );
}

function MarketingFooter() {
  return (
    <footer className="border-t border-border bg-card" data-testid="marketing-footer">
      <div className="mx-auto max-w-7xl px-6 py-14 lg:px-8">
        <div className="grid gap-10 md:grid-cols-[1.5fr_1fr_1fr_1fr]">
          <div>
            <LogoWordmark size={40} />
            <p className="mt-4 max-w-xs text-sm text-muted-foreground">
              Extract data. Govern every limit. White-label it, run your own LLM, deploy to your servers — next to Pravadh IQ and PM.
            </p>
            <LabsLink
              testid="footer-labs-url"
              className="mt-3 inline-block text-sm font-medium text-foreground hover:underline"
            >
              pravadhlabs.com
            </LabsLink>
          </div>

          <div>
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Product
            </p>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/features" className="text-muted-foreground transition-colors hover:text-foreground" data-testid="footer-features">
                  Features
                </Link>
              </li>
              <li>
                <Link to="/compare" className="text-muted-foreground transition-colors hover:text-foreground" data-testid="footer-compare">
                  Compare
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-muted-foreground transition-colors hover:text-foreground" data-testid="footer-pricing">
                  Pricing
                </Link>
              </li>
              <li>
                <Link to="/security" className="text-muted-foreground transition-colors hover:text-foreground" data-testid="footer-security">
                  Security
                </Link>
              </li>
              <li>
                <Link to="/deploy" className="text-muted-foreground transition-colors hover:text-foreground" data-testid="footer-deploy">
                  Deploy on your servers
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-muted-foreground transition-colors hover:text-foreground" data-testid="footer-terms">
                  Terms
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Labs
            </p>
            <ul className="space-y-3 text-sm">
              <li>
                <LabsLink
                  testid="footer-labs"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  Pravadh Labs
                </LabsLink>
              </li>
              {SUITE.filter((p) => !p.current).map((p) => (
                <li key={p.id}>
                  <a
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground transition-colors hover:text-foreground"
                    data-testid={`footer-${p.id}`}
                  >
                    {p.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Get started
            </p>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/signup" className="text-muted-foreground transition-colors hover:text-foreground" data-testid="footer-signup">
                  Start free trial
                </Link>
              </li>
              <li>
                <Link to="/login" className="text-muted-foreground transition-colors hover:text-foreground" data-testid="footer-login">
                  Log in
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-border pt-6 sm:flex-row">
          <div className="flex items-center gap-2">
            <LogoMark size={22} />
            <span className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} Pravadh GovernX. All rights reserved.
            </span>
          </div>
          <div className="flex items-center gap-5">
            <Link
              to="/cookie-policy"
              className="text-xs text-muted-foreground transition-colors hover:text-foreground"
              data-testid="footer-cookie-policy"
            >
              Cookie Policy
            </Link>
            <button
              type="button"
              onClick={openCookiePreferences}
              className="text-xs text-muted-foreground transition-colors hover:text-foreground"
              data-testid="footer-cookie-settings"
            >
              Cookie settings
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
